import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import Layout from "../../components/Layout";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const mkIcon = (color) => L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});
const ICONS = { manual: mkIcon("hsl(152,60%,28%)"), unverified: mkIcon("hsl(40,92%,42%)"), clustered: mkIcon("hsl(0,70%,48%)") };

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
    else if (points.length === 1) map.setView(points[0], 14);
  }, [points, map]);
  return null;
};

export default function PropertiesMap() {
  const [props_, setProps] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/properties").then((r) => setProps(r.data)).catch(() => setProps([])); }, []);
  const pinned = (props_ || []).filter((p) => p.lat != null && p.lng != null && p.active !== false);
  const points = pinned.map((p) => [p.lat, p.lng]);
  const nClustered = pinned.filter((p) => p.pin_clustered).length;
  const nUnverified = pinned.filter((p) => p.pin_unverified).length;

  return (
    <Layout title="Portfolio map">
      <div className="mb-3 flex flex-wrap gap-2 text-xs font-bold" data-testid="map-legend">
        <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">{pinned.filter((p) => !p.pin_unverified && !p.pin_clustered).length} verified pins</span>
        <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700 dark:bg-amber-950 dark:text-amber-400" data-testid="map-unverified-count">{nUnverified} unverified geocodes</span>
        <span className="rounded-full bg-destructive/10 px-3 py-1 text-destructive" data-testid="map-clustered-count">{nClustered} clustered (possible bad pins)</span>
      </div>
      {props_ && pinned.length === 0 && (
        <div className="card-flat p-6 text-center text-sm text-muted-foreground" data-testid="map-empty">No pinned properties yet — open a property and drop its pin.</div>
      )}
      <div className="overflow-hidden rounded-md border border-border" data-testid="portfolio-map">
        <MapContainer center={[44.35, -79.5]} zoom={8} style={{ height: 520, zIndex: 0 }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
          <FitBounds points={points} />
          {pinned.map((p) => (
            <Marker key={p.id} position={[p.lat, p.lng]}
              icon={p.pin_clustered ? ICONS.clustered : p.pin_unverified ? ICONS.unverified : ICONS.manual}
              eventHandlers={{ click: () => nav(`/properties/${p.id}`) }}>
              <Tooltip permanent direction="top" offset={[0, -10]} className="!text-xs !font-bold">
                {p.name}
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Tap any pin to open the property and fix its location. Red = two pins within 50m (classic street-centroid geocode); amber = geocoded but never confirmed by you.</p>
    </Layout>
  );
}
